import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { api } from './api.js';

/**
 * Per-tenant module on/off flags (the `modules` tenant setting). Persisted to
 * sessionStorage — like authStore — so a refresh doesn't flash the disabled
 * sections back in. Tagged with `tenantId` so a different tenant in the same
 * tab never reads stale flags (reads ignore a mismatched tenant until reload).
 */
interface ModulesState {
  tenantId: string | null;
  /** null = not loaded for this tenant yet ⇒ treat everything as enabled. */
  enabled: Record<string, boolean> | null;
  setModules(tenantId: string, enabled: Record<string, boolean>): void;
  clear(): void;
}

export const useModulesStore = create<ModulesState>()(
  persist(
    (set) => ({
      tenantId: null,
      enabled: null,
      setModules: (tenantId, enabled) => set({ tenantId, enabled }),
      clear: () => set({ tenantId: null, enabled: null }),
    }),
    { name: 'erp-modules', storage: createJSONStorage(() => sessionStorage) },
  ),
);

let inFlight: Promise<void> | null = null;

/**
 * Fetch the tenant's effective module flags. A module is OFF when the tenant
 * turned it off (`modules.<key> === false`) OR the platform locked it off
 * (`modules_locked.<key> === true`). Always refetches (deduped while in-flight)
 * so a platform lock change shows on the next app load, not only after a fresh
 * login. On any failure, defaults to all-enabled.
 */
export async function loadModules(tenantId: string): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      // `/settings/modules`, NOT `/settings`. The latter needs `settings.read`,
      // which a cashier or a warehouse keeper does not hold — they got a 403,
      // fell into the catch below, and an empty map reads as ALL ENABLED. So
      // every one of them was shown a menu of every module the shop had turned
      // off, each item leading to a 404. The server also does the combining now,
      // so the menu and the routers cannot disagree about what "off" means.
      const r = await api().get<{ enabled: Record<string, boolean> }>('/settings/modules');
      useModulesStore.getState().setModules(tenantId, r.data.enabled ?? {});
    } catch {
      useModulesStore.getState().setModules(tenantId, {});
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/**
 * Reactive selector: the enabled map for the current tenant, or null when it
 * isn't loaded for that tenant yet (caller treats null as "all enabled").
 */
export function useEnabledModules(
  tenantId: string | null | undefined,
): Record<string, boolean> | null {
  return useModulesStore((s) => (tenantId && s.tenantId === tenantId ? s.enabled : null));
}
